import React, { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import { useNavigate, Link, useParams, useLocation } from 'react-router-dom';
import { BypassLayout, DesktopPagination } from '../components/common';
import { useAuth } from '../context/AuthContext';
import ProblemEditModal from '../components/ProblemEdit/ProblemEditModal';
import ProblemPreview from '../components/ProblemPreview';
import { Button, Modal, BulkLabelModal } from '../components/common';
import SolutionEditModal from '../components/ProblemSolution/SolutionEditModal';
import AnswerModal from '../components/ProblemSolution/AnswerModal';
import { Trash2, Edit2, Eye, Target, FileText, Plus } from 'lucide-react';
import FloatingButton from '../components/common/FloatingButton';
import 'katex/dist/katex.min.css';
import { InlineMath } from 'react-katex';
import katex from 'katex';
import SolutionRegisterModal from '../components/ProblemSolution/SolutionRegisterModal';
import { createEditor } from 'lexical';
import { $generateHtmlFromNodes } from '@lexical/html';
import PlaygroundNodes from '../editor/nodes/PlaygroundNodes';
import { processMathEquation } from '../components/utils/latexParser';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

// 디버깅 로그 함수 (나중에 쉽게 제거하기 위해)
const debugLog = (emoji, message, data = null) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(`${emoji} ${message}`, data || '');
  }
};

// LabelEditModal 컴포넌트 추가
const LabelEditModal = ({ isOpen, onClose, problemId, initialLabels, onSave }) => {
  const [labels, setLabels] = useState(initialLabels);
  const inputRef = useRef(null);
  
  // initialLabels가 변경될 때마다 labels 상태 업데이트
  useEffect(() => {
    setLabels(initialLabels);
  }, [initialLabels]);
  
  // 모달이 열릴 때 input에 포커스하고 커서를 끝으로 이동
  useEffect(() => {
    if (isOpen) {
      // setTimeout을 사용하여 DOM이 완전히 렌더링된 후 실행
      const timer = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          // 커서를 텍스트 끝으로 이동
          const length = inputRef.current.value.length;
          inputRef.current.setSelectionRange(length, length);
          // 한 번 더 시도 (일부 브라우저에서 필요할 수 있음)
          requestAnimationFrame(() => {
            if (inputRef.current) {
              inputRef.current.setSelectionRange(length, length);
            }
          });
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [isOpen]); // 모달이 열릴 때만 실행
  
  const handleSave = () => {
    onSave(problemId, labels);
    onClose();
  };
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="라벨 수정"
      size="md"
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button
            variant="secondary"
            onClick={onClose}
          >
            취소
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            className="bg-headerBg hover:bg-gray-700"
          >
            저장
          </Button>
        </div>
      }
    >
      <div className="mb-4">
        <label className="mb-2 block text-sm font-medium text-gray-700">
          라벨 (쉼표로 구분)
        </label>
        <input
          ref={inputRef}
          type="text"
          value={labels}
          onChange={(e) => setLabels(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSave();
            }
          }}
          className="w-full rounded-3xl border p-2 focus:border-primary focus:ring-primary"
        />
      </div>
    </Modal>
  );
};

function ProblemEdit() {
  const { getAccessToken, handleApiError } = useAuth();
  const navigate = useNavigate();
  const { page_number } = useParams();
  const location = useLocation();
  const [showModal, setShowModal] = useState(false);
  const [selectedProblem, setSelectedProblem] = useState(null);
  const [problems, setProblems] = useState([]);
  const [page, setPage] = useState(parseInt(page_number) || 1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // 필터 상태
  const [filters, setFilters] = useState({
    status: '',
    author: '',
    labels: ''
  });
  
  // 최신 filters 값을 참조하기 위한 ref
  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const [isFilterMode, setIsFilterMode] = useState(false);

  // state에 totalPages 추가
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // problem_id 검색을 위한 상태 추가
  const [problemIdSearch, setProblemIdSearch] = useState('');
  const [singleProblemResult, setSingleProblemResult] = useState(null);

  const [previewInfo, setPreviewInfo] = useState({
    isOpen: false,
    problemId: [],
    problemNumber: null,
    isCorrect: null
  });

  // 라벨 수정 모달 상태 추가
  const [labelModalInfo, setLabelModalInfo] = useState({
    isOpen: false,
    problemId: null,
    labels: ''
  });

  // 풀이 관련 상태 추가
  const [showSolutionModal, setShowSolutionModal] = useState(false);
  const [selectedSolution, setSelectedSolution] = useState(null);
  const [showAnswerModal, setShowAnswerModal] = useState(false);
  const [selectedProblemId, setSelectedProblemId] = useState(null);
  const [showSolutionRegisterModal, setShowSolutionRegisterModal] = useState(false);
  const [showBulkLabelModal, setShowBulkLabelModal] = useState(false);
  
  // 드래그 감지를 위한 상태 추가
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  const [dragStartTime, setDragStartTime] = useState(0);

  // 툴팁 상태 추가
  const [tooltip, setTooltip] = useState({
    show: false,
    text: '',
    x: 0,
    y: 0,
    isHtml: false
  });
  const [solutionRegisterProblemId, setSolutionRegisterProblemId] = useState(null);

  // 인피니티 스크롤을 위한 상태 추가
  const [totalCount, setTotalCount] = useState(0);
  
  // AI 문제 상태를 저장할 상태 추가
  const [aiProblemStatuses, setAiProblemStatuses] = useState({});
  
  // 스크롤 컨테이너 ref
  const scrollContainerRef = useRef(null);
  const tableContainerRef = useRef(null);
  
  // 문제 내용 미리보기 관련 상태 추가
  const [problemContents, setProblemContents] = useState({});
  const [loadingContents, setLoadingContents] = useState({});
  
  // 드래그 플래그
  const dragRef = useRef(false);

  // 스크롤 컨테이너 참조

  // 뷰 모드 확인 헬퍼 함수
  const getViewMode = useCallback(() => {
    const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isMobileView = window.innerWidth < 768;
    return {
      isMobile: isMobileView || isMobileDevice,
      isDesktop: !isMobileView && !isMobileDevice
    };
  }, []);

  // 화면 크기에 따라 표시할 수 있는 행 수 계산
  const calculateItemsPerPage = useCallback(() => {
    try {
      const windowWidth = window.innerWidth;
      
      // 실제 모바일 기기인지 빠르게 체크
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isMobileView = windowWidth < 768;
      
      // 모바일에서는 고정값 즉시 반환 (성능 최적화)
      if (isMobileView && isMobileDevice) {
        return 5;
      }
      
      // 데스크톱과 데스크톱 반응형은 기존 로직 유지
      const windowHeight = window.innerHeight;
      const isDesktopResponsive = windowWidth < 768 && !isMobileDevice && windowWidth >= 375;
      
      let fixedHeight, tableHeader, paginationArea, margins, rowHeight;
      
      if (isDesktopResponsive) {
        // 데스크톱에서 모바일 반응형으로 전환된 경우
        fixedHeight = 3 * 16; // 3rem (48px)
        tableHeader = 35; // 테이블 헤더도 줄임
        paginationArea = 25; // 페이지네이션 더 줄임
        margins = 0;
        rowHeight = 67; // ProblemEdit는 problem-row + solution-row 두 줄
      } else {
        // 데스크톱
        fixedHeight = 14 * 16; // 14rem
        tableHeader = 40;
        paginationArea = 60;
        margins = 0;
        rowHeight = 67; // 실제 측정된 두 줄의 높이
      }
      
      const availableHeight = windowHeight - fixedHeight - tableHeader - paginationArea - margins;
      const itemsCount = Math.floor(availableHeight / rowHeight);
      
      // 세로 스크롤이 생기지 않도록 계산된 값 그대로 사용
      // 최소값은 1로 설정하여 항상 최소 1개는 표시
      const minItems = 1;
      const maxItems = isMobileView ? 30 : 50;
      
      // 계산된 itemsCount가 1보다 작으면 1을 반환
      return Math.max(minItems, Math.min(itemsCount, maxItems));
    } catch (error) {
      console.error('Error calculating items per page:', error);
      return 10; // 기본값
    }
  }, []);

  // fetchWithTokenRefresh 함수 수정
  const fetchWithTokenRefresh = useCallback(async (url, options = {}) => {
    try {
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return null;
      }

      const response = await fetch(`${API_BASE_URL}${url}`, {
        ...options,
        headers: {
          ...options.headers,
          'Authorization': token,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      // 401 응답 코드 체크 추가
      if (response.status === 401) {
        handleApiError(new Error('Invalid or expired token'));
        return null;
      }

      const data = await response.json();

      // 응답 데이터의 status_code도 체크
      if (data.status_code === 401) {
        handleApiError(new Error('Invalid or expired token'));
        return null;
      }

      return data;
    } catch (error) {
      console.error('API 호출 에러:', error);
      if (error.message.includes('token') || error.message.includes('unauthorized')) {
        handleApiError(error);
      }
      throw error;
    }
  }, [getAccessToken, navigate, handleApiError]);

  // Lexical 데이터를 HTML로 변환하는 함수
  const convertLexicalToHtml = useCallback((lexicalData) => {
    const editor = createEditor({
      nodes: [...PlaygroundNodes]
    });

    const processNode = (node) => {
      if (node.type === 'equation') {
        node.equation = processMathEquation(node.equation);
      } else if (node.type === 'box-content') {
        node.className = 'PlaygroundEditorTheme__boxContent';
      }
      if (node.children && Array.isArray(node.children)) {
        node.children = node.children.map(child => processNode(child));
      }
      return node;
    };

    lexicalData.root = processNode(lexicalData.root);
    const editorState = editor.parseEditorState(lexicalData);

    let content = '';
    editor.setEditorState(editorState);
    editor.update(() => {
      content = $generateHtmlFromNodes(editor);
    });

    return content;
  }, []);

  // 모바일용 문제 내용 배치 로드 함수 (print API 사용)
  const loadProblemsContent = useCallback(async (problemIds) => {
    // 데스크톱에서는 문제 내용 로딩하지 않음
    const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isMobileView = window.innerWidth < 768;
    
    // 모바일 뷰이거나 모바일 기기 중 하나라도 해당하면 실행 (OR 조건)
    if (!isMobileView && !isMobileDevice) {
      return;
    }
    if (!problemIds || problemIds.length === 0) {
      return;
    }
    
    // 이미 로딩된 문제들은 제외
    const problemsToLoad = problemIds.filter(id => !problemContents[id] && !loadingContents[id]);
    if (problemsToLoad.length === 0) return;
    
    // 로딩 상태 설정
    setLoadingContents(prev => {
      const newState = { ...prev };
      problemsToLoad.forEach(id => {
        newState[id] = true;
      });
      return newState;
    });
    
    try {
      const problemIdsString = problemsToLoad.join(',');
      const response = await fetchWithTokenRefresh(`/api/problems/print?problem_ids=${problemIdsString}`);
      
      if (response?.status === 'success' && response.data) {
        const newContents = {};
        response.data.forEach(problem => {
          if (problem.lexical_data?.lexical_data) {
            const htmlContent = convertLexicalToHtml(problem.lexical_data.lexical_data);
            newContents[problem.problem_id] = htmlContent;
          } else {
            newContents[problem.problem_id] = '문제 내용을 불러올 수 없습니다.';
          }
        });
        
        // 응답에 없는 문제들 처리
        problemsToLoad.forEach(problemId => {
          if (!newContents[problemId]) {
            newContents[problemId] = '문제 내용을 불러올 수 없습니다.';
          }
        });
        
        setProblemContents(prev => ({ ...prev, ...newContents }));
      }
    } catch (error) {
      console.error('Error loading problem contents:', error);
      // 에러 발생 시 에러 메시지 설정
      const errorContents = {};
      problemsToLoad.forEach(id => {
        errorContents[id] = '문제 내용을 불러올 수 없습니다.';
      });
      setProblemContents(prev => ({ ...prev, ...errorContents }));
    } finally {
      // 로딩 상태 해제
      setLoadingContents(prev => {
        const newState = { ...prev };
        problemsToLoad.forEach(id => {
          delete newState[id];
        });
        return newState;
      });
    }
  }, [fetchWithTokenRefresh, convertLexicalToHtml]);

  // fetchProblems 호출 추적을 위한 ref
  const fetchingRef = useRef(false);
  
  // API 호출 함수 수정 - 인피니티 스크롤 지원
  const fetchProblems = useCallback(async (pageNum = 1, useFilter = false, isAppend = false, filterValues = null) => {
    console.log('🚀 fetchProblems called:', { pageNum, useFilter, isAppend, filterValues }); // 디버깅 로그
    console.log('📊 Current state:', { loading, isLoadingMore, fetchingFlag: fetchingRef.current }); // 디버깅 로그
    
    // 더 엄격한 중복 방지 로직
    if (fetchingRef.current) {
      console.log('❌ fetchProblems blocked - fetchingRef is true'); // 디버깅 로그
      return;
    }
    
    if (loading && !isAppend) {
      console.log('❌ fetchProblems blocked - already loading'); // 디버깅 로그
      return;
    }
    
    if (isAppend && isLoadingMore) {
      console.log('❌ fetchProblems blocked - already loading more'); // 디버깅 로그
      return;
    }
    
    console.log('✅ fetchProblems proceeding'); // 디버깅 로그
    fetchingRef.current = true;

    const shouldUseFilter = useFilter || isFilterMode;
    
    if (isAppend) {
      setIsLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      // 모바일 초기 로딩 최적화
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isMobileView = window.innerWidth < 768;
      const isInitialLoad = pageNum === 1 && !isAppend;
      
      let itemsPerPage = calculateItemsPerPage();
      
      // 모바일 초기 로딩은 5개로 제한
      if (isInitialLoad && isMobileView && isMobileDevice) {
        itemsPerPage = 5;
      }
      const params = new URLSearchParams({
        page: pageNum,
        per_page: itemsPerPage
      });

      // 통합 API 사용 - answer, difficulty, solution 데이터를 한 번에 요청
      params.append('answer', 'true');
      params.append('difficulty', 'true');
      params.append('solution', 'true');

      // 모바일에서는 경량화된 데이터 요청
      if (isMobileView && isMobileDevice) {
        params.append('mobile', 'true');
        params.append('fields', 'problem_id,labels,status,author,created_at,updated_at'); // 필수 필드만 요청
      }

      // 전달받은 filterValues 사용, 없으면 현재 filtersRef 값 사용
      const currentFilters = filterValues || filtersRef.current;
      console.log('🔍 Using filters:', currentFilters); // 디버깅 로그
      
      if (shouldUseFilter) {
        if (currentFilters.status) params.append('status', currentFilters.status);
        if (currentFilters.author) params.append('author', currentFilters.author);
        if (currentFilters.labels) params.append('labels', currentFilters.labels);
      }

      const hasFilterValues = currentFilters.status || currentFilters.author || currentFilters.labels;
      const endpoint = shouldUseFilter && hasFilterValues ?
        '/api/problems/filtered' :
        '/api/problems/';

      const response = await fetchWithTokenRefresh(`${endpoint}?${params}`);

      if (!response) return;

      if (response.status === 'success') {
        // 통합 API 응답 데이터를 기존 형식에 맞게 변환
        const problemsWithAllData = response.data.map(problem => {
          // 솔루션 데이터 변환 (기존 형식 유지)
          // sol_ 필드가 있으면 사용하고, 없으면 기존 문제 정보를 사용
          const solutionData = problem.solution_status !== undefined ? [{
            solution_info: {
              status: problem.solution_status || 0,
              answer: problem.answer || null,
              created_at: problem.sol_created_at || problem.created_at,
              updated_at: problem.sol_updated_at || problem.updated_at,
              author: problem.sol_author || problem.author
            }
          }] : [];

          return {
            ...problem,
            // 솔루션 관련 데이터
            has_solution: (problem.solution_status || 0) >= 100,
            solution_data: solutionData,
            // answer 필드는 이미 API에서 제공됨
          };
        });
        
        // AI 문제 상태 설정 (통합 API에서 받은 difficulty 데이터 사용)
        const newAiStatuses = response.data.reduce((acc, problem) => {
          if (problem.difficulty !== undefined) {
            acc[problem.problem_id] = {
              status: problem.status,
              difficulty: problem.difficulty,
              concept_ids: [] // 통합 API에서 concept_ids를 제공하지 않는 경우 빈 배열
            };
          }
          return acc;
        }, {});
        
        // 기존 AI 상태와 병합 (append 모드일 때)
        if (isAppend) {
          setAiProblemStatuses(prev => ({ ...prev, ...newAiStatuses }));
        } else {
          setAiProblemStatuses(newAiStatuses);
        }

        if (isAppend) {
          setProblems(prev => [...prev, ...problemsWithAllData]);
        } else {
          setProblems(problemsWithAllData);
          setTotalCount(response.total_count || response.data.length);
          setTotalPages(response.total_pages || 1);
          setTotalItems(response.total_items || response.total_count || response.data.length);
        }
        
        // 모바일에서만 문제 내용을 배치로 비동기 로드 (UI 블로킹 방지)
        if (isMobileView || isMobileDevice) {
          const problemIds = problemsWithAllData.map(p => p.problem_id);
          loadProblemsContent(problemIds);
        }
        
        setPage(pageNum);
        setHasMore(pageNum < response.total_pages);
      }
    } catch (error) {
      console.error('문제 목록 불러오기 실패:', error);
    } finally {
      // 모든 플래그를 안전하게 리셋
      fetchingRef.current = false;
      if (isAppend) {
        setIsLoadingMore(false);
      } else {
        setLoading(false);
      }
      // 만약의 상황에 대비해 다른 플래그도 체크
      if (loading && !isAppend) {
        setLoading(false);
      }
      if (isLoadingMore && isAppend) {
        setIsLoadingMore(false);
      }
    }
  }, [isFilterMode, fetchWithTokenRefresh, calculateItemsPerPage, loadProblemsContent]);

  // URL 업데이트 함수
  const updateURL = (pageNum) => {
    navigate(`/problem-edit/${pageNum}`);
  };

  // 모바일 인피니티 스크롤 핸들러 (쓰로틀링 적용)
  const scrollTimerRef = useRef(null);
  const handleMobileScroll = useCallback((e) => {
    if (loading || isLoadingMore || !hasMore) return;
    
    // 쓰로틀링: 100ms 간격으로만 실행
    if (scrollTimerRef.current) return;
    
    scrollTimerRef.current = setTimeout(() => {
      scrollTimerRef.current = null;
      
      const container = e.target;
      const { scrollTop, scrollHeight, clientHeight } = container;
      
      // 스크롤이 하단에서 200px 이내에 도달했을 때 (모바일에서 더 일찍 트리거)
      if (scrollHeight - scrollTop - clientHeight < 200) {
        console.log('Mobile infinite scroll triggered - loading page:', page + 1);
        fetchProblems(page + 1, isFilterMode, true);
      }
    }, 100);
  }, [loading, isLoadingMore, hasMore, page, isFilterMode, fetchProblems]);

  // 컴포넌트 unmount 시 타이머 정리
  useEffect(() => {
    return () => {
      if (scrollTimerRef.current) {
        clearTimeout(scrollTimerRef.current);
      }
    };
  }, []);

  // 초기화 핸들러 - Race Condition 방지
  const handleReset = useCallback(() => {
    // 초기화 시작 시 모든 플래그 명시적으로 리셋
    fetchingRef.current = false;
    setLoading(false);
    setIsLoadingMore(false);
    
    setIsFilterMode(false);
    setFilters({
      status: '',
      author: '',
      labels: ''
    });
    setProblemIdSearch(''); // problem_id 검색어 초기화
    setSingleProblemResult(null); // 검색 결과 초기화
    setAiProblemStatuses({}); // AI 문제 상태 초기화
    setProblems([]);
    setPage(1);
    setHasMore(true);
    
    // navigate 후 약간의 지연을 두고 fetchProblems 호출 (검색 중이 아닐 때만)
    navigate('/problem-edit/1');
    setTimeout(() => {
      if (!fetchingRef.current) { // 다른 검색이 진행 중이 아닐 때만 실행
        console.log('🔄 Reset: calling fetchProblems'); // 디버깅 로그
        fetchProblems(1, false);
      } else {
        console.log('⏸️ Reset: skipping fetchProblems (already fetching)'); // 디버깅 로그
      }
    }, 50); // 50ms 지연으로 navigate 완료 대기
  }, [navigate, fetchProblems]);

  // 필터 조회 핸들러 수정
  const handleSearch = useCallback(() => {
    console.log('🔍 handleSearch called'); // 디버깅 로그
    console.log('📋 Current filters state:', filters); // 실제 filters 상태 확인
    console.log('📋 Current filtersRef:', filtersRef.current); // ref 값도 확인
    
    // 실제 filters 상태를 사용하여 검색 값 확인
    const hasFilterValues = filters.status || filters.author || filters.labels;
    if (!hasFilterValues) {
      alert('상태, 작성자, 라벨 중 하나 이상을 입력해주세요.');
      return;
    }
    
    console.log('🔄 Resetting flags and starting search'); // 디버깅 로그
    
    // 모든 상태를 동기적으로 리셋 (React의 배치 업데이트 방지를 위해 즉시 실행)
    fetchingRef.current = false;
    
    setIsFilterMode(true);
    setProblems([]);
    setPage(1);
    setHasMore(true);
    
    // 상태 리셋을 확실히 한 후 검색 실행
    setTimeout(() => {
      setLoading(false);
      setIsLoadingMore(false);
      fetchingRef.current = false; // 한 번 더 확실히 리셋
      console.log('🎯 Starting search with timeout'); // 디버깅 로그
      fetchProblems(1, true, false, filters);
    }, 10); // 매우 짧은 지연으로 상태 업데이트 완료 보장
  }, [filters, fetchProblems]);

  // problem_id로 단일 문제 검색하는 함수 추가
  const handleProblemIdSearch = useCallback(async () => {
    if (!problemIdSearch || isNaN(problemIdSearch)) {
      alert('유효한 문제 ID를 입력해주세요.');
      return;
    }

    // ID 검색 시작 시 모든 플래그 명시적으로 리셋
    fetchingRef.current = false;
    setLoading(false);
    setIsLoadingMore(false);

    try {
      // 1. 기본 문제 정보 가져오기 (solution 정보도 함께 요청)
      const response = await fetchWithTokenRefresh(`/api/problems/${problemIdSearch}?difficulty=true&solution=true&answer=true`);
      
      if (!response || response.status !== 'success') {
        alert('문제를 찾을 수 없습니다.');
        setSingleProblemResult(null);
        setProblems([]);
        return;
      }

      const problemData = response.data;
      
      // 2. 솔루션 정보 별도로 가져오기 (상세 정보가 필요한 경우)
      let solutionData = [];
      let answerValue = null;
      
      // API에서 sol_ 필드를 제공하면 우선 사용
      if (problemData.sol_author || problemData.sol_created_at || problemData.sol_updated_at) {
        solutionData = [{
          solution_info: {
            status: problemData.solution_status || 0,
            answer: problemData.answer || null,
            created_at: problemData.sol_created_at || problemData.created_at,
            updated_at: problemData.sol_updated_at || problemData.updated_at,
            author: problemData.sol_author || problemData.author
          }
        }];
        answerValue = problemData.answer || null;
      } else {
        // sol_ 필드가 없으면 기존 방식으로 솔루션 정보 가져오기
        try {
          const solutionsResponse = await fetchWithTokenRefresh(`/api/solutions/list/${problemIdSearch}`);
          if (solutionsResponse?.status === 'success' && solutionsResponse.data && solutionsResponse.data.length > 0) {
            solutionData = solutionsResponse.data;
            // 첫 번째 솔루션에서 답변 추출
            answerValue = solutionData[0]?.solution_info?.answer || null;
          }
        } catch (solutionError) {
          console.warn('솔루션 정보 로드 실패:', solutionError);
          // 솔루션 로드 실패해도 문제 정보는 표시
        }
      }
        
      // 3. 응답 구조에 맞게 데이터 구조 변환
      const formattedProblem = {
        problem_id: problemData.problem_id,
        author: problemData.author || problemData.problem_info?.author,
        status: problemData.status || problemData.problem_info?.status,
        labels: problemData.labels || problemData.problem_info?.labels,
        created_at: problemData.created_at || problemData.problem_info?.created_at,
        updated_at: problemData.updated_at || problemData.problem_info?.updated_at,
        // 솔루션 데이터
        has_solution: (problemData.solution_status || 0) >= 100 || (solutionData.length > 0 && solutionData[0]?.solution_info?.status >= 100),
        solution_data: solutionData,
        // 정답 필드
        answer: answerValue
      };
      
      // 4. AI 문제 상태 설정
      if (problemData.difficulty !== undefined) {
        setAiProblemStatuses({
          [problemData.problem_id]: {
            status: problemData.status || problemData.problem_info?.status,
            difficulty: problemData.difficulty,
            concept_ids: []
          }
        });
      } else {
        setAiProblemStatuses({});
      }
      
      setProblems([formattedProblem]); // 테이블에 단일 결과만 표시
      setSingleProblemResult(response.data);
      setTotalPages(1);
      
      // 5. 모바일에서 문제 내용 미리보기 로드
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isMobileView = window.innerWidth < 768;
      if (isMobileView || isMobileDevice) {
        loadProblemsContent([problemData.problem_id]);
      }
    } catch (error) {
      console.error('문제 검색 실패:', error);
      alert('문제 검색에 실패했습니다.');
    }
  }, [problemIdSearch, fetchWithTokenRefresh, loadProblemsContent]);

  // 툴팁 숨기기 - 전역 클릭 이벤트
  useEffect(() => {
    const handleGlobalClick = () => {
      setTooltip({ show: false, text: '', x: 0, y: 0, isHtml: false });
    };

    document.addEventListener('click', handleGlobalClick);
    return () => {
      document.removeEventListener('click', handleGlobalClick);
    };
  }, []);

  // 키보드 단축키 - ESC (초기화), Enter (검색)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // 모달이나 input이 포커스된 경우는 제외
      const activeElement = document.activeElement;
      const isInputFocused = activeElement && (
        activeElement.tagName === 'INPUT' || 
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.contentEditable === 'true'
      );

      // ESC 키 - 초기화
      if (e.key === 'Escape') {
        debugLog('⌨️', 'ESC key pressed - resetting');
        handleReset();
        e.preventDefault();
        return;
      }

      // Enter 키 - 검색 (input 필드에서 Enter는 해당 필드의 onKeyDown으로 처리)
      if (e.key === 'Enter' && !isInputFocused) {
        // ID 검색 우선순위가 높음
        if (problemIdSearch && problemIdSearch.trim()) {
          debugLog('⌨️', 'Enter key pressed - ID search');
          handleProblemIdSearch();
          e.preventDefault();
          return;
        }
        
        // 필터 검색
        const hasFilterValues = filters.status || filters.author || filters.labels;
        if (hasFilterValues) {
          debugLog('⌨️', 'Enter key pressed - filter search');
          handleSearch();
          e.preventDefault();
          return;
        }
        
        debugLog('⌨️', 'Enter key pressed - no search criteria');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleReset, handleSearch, handleProblemIdSearch, problemIdSearch, filters]);

  // 초기 데이터 로드 수정
  useEffect(() => {
    const checkAuthAndFetch = async () => {
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin', { replace: true });
        return;
      }
      
      // 이미 다른 검색이 진행 중이면 스킵
      if (fetchingRef.current) {
        console.log('⏸️ Initial load: skipping fetchProblems (already fetching)'); // 디버깅 로그
        return;
      }
      
      const pageNum = parseInt(page_number) || 1;
      setPage(pageNum);
      console.log('🔄 Initial load: calling fetchProblems'); // 디버깅 로그
      fetchProblems(pageNum, false);
    };

    checkAuthAndFetch();
  }, [page_number, getAccessToken, navigate, fetchProblems]); // 실제 사용하는 모든 의존성 추가

  // 화면 크기 변경 시 per_page 재계산 (데스크톱만, 디바운싱 적용)  
  useEffect(() => {
    // 모바일에서는 resize 이벤트 무시
    const isMobile = window.innerWidth < 768;
    if (isMobile) return;

    let resizeTimer;
    const handleResize = () => {
      // 모바일로 전환되면 무시
      const currentWidth = window.innerWidth;
      if (currentWidth < 768) return;
      
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        // 현재 상태를 체크하여 조건부 실행 (ref를 통한 최신 상태 확인)
        if (!fetchingRef.current && !loading && !isLoadingMore) {
          console.log('🔄 Resize: calling fetchProblems'); // 디버깅 로그
          fetchProblems(page, isFilterMode);
        } else {
          console.log('⏸️ Resize: skipping fetchProblems (already fetching)'); // 디버깅 로그
        }
      }, 500); // 500ms 디바운싱
    };

    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', handleResize);
    };
  }, [fetchProblems, page, isFilterMode]); // fetchProblems와 필요한 값들만 포함

  // 상태에 따른 데이터 상태 표시 함수
  const getStatusInfo = (status) => {
    const statusMap = {
      200: { image: true, ocr: false, editor: false },
      300: { image: true, ocr: true, editor: false },
      999: { image: true, ocr: true, editor: true },
      777: { image: true, ocr: true, editor: true }  // AI 생성 문제 - 999 다음 상태
    };
    return statusMap[status] || { image: false, ocr: false, editor: false };
  };

  // 필터 변경 핸들러 수정
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };


  // 편집 버튼 핸들러
  const handleEdit = useCallback(async (problem) => {
    try {
      const response = await fetchWithTokenRefresh(`/api/problems/${problem.problem_id}?lexical=true&mathpix=true&base64=true`);

      if (!response) return;

      setSelectedProblem(response.data);
      setShowModal(true);
    } catch (error) {
      console.error('문제 데이터 불러오기 실패:', error);
      alert('문제 데이터를 불러오는데 실패했습니다.');
    }
  }, [fetchWithTokenRefresh]);

  // 날짜 포맷 함수
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    // GMT+9 시간 적용
    const koreaDate = new Date(date.getTime() + (9 * 60 * 60 * 1000));
    return `${koreaDate.getFullYear()}-${String(koreaDate.getMonth() + 1).padStart(2, '0')}-${String(koreaDate.getDate()).padStart(2, '0')} ${String(koreaDate.getHours()).padStart(2, '0')}:${String(koreaDate.getMinutes()).padStart(2, '0')}:${String(koreaDate.getSeconds()).padStart(2, '0')}`;
  };

  // 데이터 갱신 함수
  const refreshData = () => {
    setProblems([]);
    setPage(1);
    setHasMore(true);
    fetchProblems(1, isFilterMode);
  };

  // 데스크톱용 새로고침 (페이지네이션)
  const refreshDesktopPage = useCallback(async () => {
    const currentPage = page;
    
    try {
      // 로딩 상태 표시 (기존 데이터는 유지)
      setLoading(true);
      
      // API 호출
      const params = new URLSearchParams({
        page: currentPage,
        per_page: calculateItemsPerPage(),
        answer: 'true',
        difficulty: 'true',
        solution: 'true'
      });
      
      // 필터 적용
      if (isFilterMode) {
        const currentFilters = filtersRef.current;
        if (currentFilters.status) params.append('status', currentFilters.status);
        if (currentFilters.author) params.append('author', currentFilters.author);
        if (currentFilters.labels) params.append('labels', currentFilters.labels);
      }
      
      const hasFilterValues = filtersRef.current.status || filtersRef.current.author || filtersRef.current.labels;
      const endpoint = isFilterMode && hasFilterValues 
        ? '/api/problems/filtered' 
        : '/api/problems/';
      
      const response = await fetchWithTokenRefresh(`${endpoint}?${params}`);
      
      if (response?.status === 'success') {
        // 데이터 변환 로직 (기존 fetchProblems와 동일)
        const problemsWithAllData = response.data.map(problem => {
          const solutionData = problem.solution_status !== undefined ? [{
            solution_info: {
              status: problem.solution_status || 0,
              answer: problem.answer || null,
              created_at: problem.sol_created_at || problem.created_at,
              updated_at: problem.sol_updated_at || problem.updated_at,
              author: problem.sol_author || problem.author
            }
          }] : [];

          return {
            ...problem,
            has_solution: (problem.solution_status || 0) >= 100,
            solution_data: solutionData,
          };
        });
        
        // AI 문제 상태 설정
        const newAiStatuses = response.data.reduce((acc, problem) => {
          if (problem.difficulty !== undefined) {
            acc[problem.problem_id] = {
              status: problem.status,
              difficulty: problem.difficulty,
              concept_ids: []
            };
          }
          return acc;
        }, {});
        
        setAiProblemStatuses(newAiStatuses);
        
        // 현재 페이지 데이터만 교체
        setProblems(problemsWithAllData);
        setTotalPages(response.total_pages || 1);
        setTotalItems(response.total_items || response.total_count);
        
        // 페이지가 유효하지 않으면 1페이지로 이동
        if (problemsWithAllData.length === 0 && currentPage > 1) {
          updateURL(1);
          fetchProblems(1, isFilterMode);
        }
      }
    } catch (error) {
      console.error('데스크톱 페이지 새로고침 실패:', error);
    } finally {
      setLoading(false);
    }
  }, [page, calculateItemsPerPage, isFilterMode, filtersRef, fetchWithTokenRefresh, updateURL, fetchProblems]);

  // 모바일용 새로고침 (인피니티 스크롤)
  const refreshMobileWithScroll = useCallback(async () => {
    // 현재 스크롤 위치 저장
    if (scrollContainerRef.current) {
      scrollPositionRef.current = scrollContainerRef.current.scrollTop;
    }
    
    const currentLoadedPages = page; // 현재까지 로드된 페이지 수
    
    try {
      setLoading(true);
      
      // 1페이지부터 현재 페이지까지 모든 데이터 한번에 요청
      const params = new URLSearchParams({
        page: 1,
        per_page: calculateItemsPerPage() * currentLoadedPages, // 현재까지 로드된 모든 항목
        answer: 'true',
        difficulty: 'true',
        solution: 'true'
      });
      
      // 필터 적용
      if (isFilterMode) {
        const currentFilters = filtersRef.current;
        if (currentFilters.status) params.append('status', currentFilters.status);
        if (currentFilters.author) params.append('author', currentFilters.author);
        if (currentFilters.labels) params.append('labels', currentFilters.labels);
      }
      
      const hasFilterValues = filtersRef.current.status || filtersRef.current.author || filtersRef.current.labels;
      const endpoint = isFilterMode && hasFilterValues 
        ? '/api/problems/filtered' 
        : '/api/problems/';
      
      const response = await fetchWithTokenRefresh(`${endpoint}?${params}`);
      
      if (response?.status === 'success') {
        // 데이터 변환
        const problemsWithAllData = response.data.map(problem => {
          const solutionData = problem.solution_status !== undefined ? [{
            solution_info: {
              status: problem.solution_status || 0,
              answer: problem.answer || null,
              created_at: problem.sol_created_at || problem.created_at,
              updated_at: problem.sol_updated_at || problem.updated_at,
              author: problem.sol_author || problem.author
            }
          }] : [];

          return {
            ...problem,
            has_solution: (problem.solution_status || 0) >= 100,
            solution_data: solutionData,
          };
        });
        
        // AI 문제 상태 설정
        const newAiStatuses = response.data.reduce((acc, problem) => {
          if (problem.difficulty !== undefined) {
            acc[problem.problem_id] = {
              status: problem.status,
              difficulty: problem.difficulty,
              concept_ids: []
            };
          }
          return acc;
        }, {});
        
        setAiProblemStatuses(newAiStatuses);
        setProblems(problemsWithAllData);
        setPage(currentLoadedPages);
        setHasMore(currentLoadedPages < response.total_pages);
        setTotalPages(response.total_pages || 1);
        setTotalItems(response.total_items || response.total_count);
        
        // 스크롤 위치 복원 (DOM 업데이트 후)
        requestAnimationFrame(() => {
          if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = scrollPositionRef.current;
          }
        });
      }
    } catch (error) {
      console.error('모바일 인피니티 스크롤 새로고침 실패:', error);
    } finally {
      setLoading(false);
    }
  }, [page, calculateItemsPerPage, isFilterMode, filtersRef, fetchWithTokenRefresh]);

  // 현재 페이지를 유지하면서 데이터를 새로고침
  const refreshDataKeepPage = useCallback(async () => {
    const viewMode = getViewMode();
    
    if (viewMode.isDesktop) {
      // 데스크톱: 현재 페이지만 새로고침
      await refreshDesktopPage();
    } else {
      // 모바일: 스크롤 위치 저장 후 전체 새로고침
      await refreshMobileWithScroll();
    }
  }, [getViewMode, refreshDesktopPage, refreshMobileWithScroll]);

  // handleDelete 함수 추가
  const handleDelete = useCallback(async (problemId) => {
    if (!window.confirm('정말로 이 문제를 삭제하시겠습니까?')) {
      return;
    }

    try {
      const response = await fetchWithTokenRefresh(`/api/problems/${problemId}`, {
        method: 'DELETE'
      });

      if (!response) return;

      if (response.status === 'success') {
        alert('문제가 성공적으로 삭제되었습니다.');
        refreshData();
      } else {
        // 에러 상태에 따른 메시지 처리
        switch (response.status_code) {
          case 400:
            alert('잘못된 문제 ID입니다.');
            break;
          case 409:
            alert('시험지에 등록된 문제는 삭제할 수 없습니다.');
            break;
          case 410:
            alert('이미 삭제된 문제입니다.');
            break;
          default:
            alert('문제 삭제에 실패했습니다.');
        }
      }
    } catch (error) {
      console.error('문제 삭제 실패:', error);
      alert('문제 삭제 중 오류가 발생했습니다.');
    }
  }, [fetchWithTokenRefresh, refreshData]);

  // 페이지네이션 렌더링 함수 제거 (인피니티 스크롤로 대체)
  // const renderPageNumbers = () => { ... };

  // 테이블의 미리보기 버튼 클릭 핸들러 수정
  const handlePreview = (problem) => {
    // 드래그 중이면 미리보기 실행하지 않음
    if (dragRef.current) return;
    
    setPreviewInfo({
      isOpen: true,
      problemId: [problem.problem_id],
      problemNumber: null,  // 문제 수정 페이지에서는 번호 표시 안 함
      isCorrect: null      // 문제 수정 페이지에서는 정답 여부 없음
    });
  };
  
  // 마우스 다운 핸들러
  const handleMouseDown = useCallback((e) => {
    setDragStartPos({ x: e.clientX, y: e.clientY });
    setDragStartTime(Date.now());
    dragRef.current = false;
  }, []);

  // 마우스 무브 핸들러
  const handleMouseMove = useCallback((e) => {
    if (!dragStartTime) return;
    
    const moveDistance = Math.sqrt(
      Math.pow(e.clientX - dragStartPos.x, 2) + 
      Math.pow(e.clientY - dragStartPos.y, 2)
    );
    
    // 5픽셀 이상 움직이면 드래그로 판단
    if (moveDistance > 5) {
      dragRef.current = true;
    }
  }, [dragStartPos, dragStartTime]);

  // 마우스 업 핸들러
  const handleMouseUp = useCallback((e, problem) => {
    const timeDiff = Date.now() - dragStartTime;
    const moveDistance = Math.sqrt(
      Math.pow(e.clientX - dragStartPos.x, 2) + 
      Math.pow(e.clientY - dragStartPos.y, 2)
    );
    
    // 클릭으로 판단: 이동 거리가 5픽셀 미만이고 시간이 200ms 미만
    if (moveDistance < 5 && timeDiff < 200 && !dragRef.current) {
      handlePreview(problem);
    }
    
    // 상태 초기화
    dragRef.current = false;
    setDragStartPos({ x: 0, y: 0 });
    setDragStartTime(0);
  }, [dragStartPos, dragStartTime]);

  // 라벨 수정 핸들러 추가
  const handleLabelEdit = (problem) => {
    setLabelModalInfo({
      isOpen: true,
      problemId: problem.problem_id,
      labels: problem.labels
    });
  };
  
  // 라벨 저장 중복 방지를 위한 ref
  const isSavingLabelRef = useRef(false);
  
  // 스크롤 위치 저장용 ref 추가
  const scrollPositionRef = useRef(0);
  
  // 라벨 저장 핸들러 추가
  const handleLabelSave = async (problemId, newLabels) => {
    // 이미 저장 중이면 무시
    if (isSavingLabelRef.current) return;
    
    isSavingLabelRef.current = true;
    
    try {
      const response = await fetchWithTokenRefresh('/api/problems/update', {
        method: 'PUT',
        body: JSON.stringify({
          problems: [{
            problem_id: problemId,
            labels: newLabels
          }]
        })
      });

      if (!response) {
        isSavingLabelRef.current = false;
        return;
      }

      alert('라벨이 수정되었습니다.');
      refreshDataKeepPage(); // 현재 페이지를 유지하면서 데이터 새로고침
    } catch (error) {
      console.error('라벨 수정 실패:', error);
      alert('라벨 수정에 실패했습니다.');
    } finally {
      // 잠시 후 플래그 리셋 (연속 저장 가능하도록)
      setTimeout(() => {
        isSavingLabelRef.current = false;
      }, 500);
    }
  };

  // 풀이 관련 상태 표시 함수
  const getSolutionStatusInfo = (status) => {
    const statusMap = {
      100: { answer: true, image: false, ocr: false, editor: false },
      200: { answer: true, image: true, ocr: false, editor: false },
      300: { answer: true, image: true, ocr: true, editor: false },
      999: { answer: true, image: true, ocr: true, editor: true }
    };
    return statusMap[status] || { answer: false, image: false, ocr: false, editor: false };
  };

  // 풀이 편집 핸들러
  const handleSolutionEdit = useCallback(async (problemId) => {
    try {
      // 1. 먼저 solution 목록을 가져옴
      const solutionsResponse = await fetchWithTokenRefresh(`/api/solutions/list/${problemId}`);
      
      if (!solutionsResponse || !solutionsResponse.data || solutionsResponse.data.length === 0) {
        alert('해당 문제의 풀이가 없습니다.');
        return;
      }

      // 가장 작은 solution_id를 가진 solution 선택
      const solution = solutionsResponse.data.reduce((prev, current) => 
        prev.solution_id < current.solution_id ? prev : current
      );

      // 2. solution의 status에 따라 파라미터 설정
      const params = new URLSearchParams();
      params.append('base64', 'true');
      
      if (solution.solution_info.status >= 300) {
        params.append('mathpix', 'true');
      }
      
      if (solution.solution_info.status >= 999) {
        params.append('lexical', 'true');
      }

      // 3. solution 상세 정보 가져오기
      const solutionResponse = await fetchWithTokenRefresh(
        `/api/solutions/${solution.solution_id}?${params}`
      );

      if (!solutionResponse) return;

      setSelectedSolution(solutionResponse.data);
      setShowSolutionModal(true);
    } catch (error) {
      console.error('해답 데이터 불러오기 실패:', error);
      alert('해답 데이터를 불러오는데 실패했습니다.');
    }
  }, [fetchWithTokenRefresh]);

  // 정답 등록 핸들러
  const handleAnswerRegister = useCallback(async (problemId) => {
    setSelectedProblemId(problemId);
    setShowAnswerModal(true);
  }, []);

  // 정답 수정 핸들러
  const handleAnswerEdit = useCallback(async (problemId) => {
    try {
      const solutionsResponse = await fetchWithTokenRefresh(`/api/solutions/list/${problemId}`);
      if (!solutionsResponse || !solutionsResponse.data || solutionsResponse.data.length === 0) {
        alert('해당 문제의 풀이가 없습니다.');
        return;
      }

      const solution = solutionsResponse.data[0];
      setSelectedSolution(solution);
      setShowAnswerModal(true);
    } catch (error) {
      console.error('해답 데이터 불러오기 실패:', error);
      alert('해답 데이터를 불러오는데 실패했습니다.');
    }
  }, [fetchWithTokenRefresh]);

  // 풀이 등록 핸들러
  const handleSolutionRegister = (problemId) => {
    setSolutionRegisterProblemId(problemId);
    setShowSolutionRegisterModal(true);
  };

  return (
    <BypassLayout
      loading={loading && problems.length === 0}
      renderContent={({ isMobileView, getSafeAreaStyle }) => (
        <div 
          className={`flex flex-col ${isMobileView ? 'gap-4' : ''} ${
            isMobileView 
              ? 'h-full overflow-y-auto' 
              : 'h-[calc(100vh-2rem-50px)]'
          }`}
          style={isMobileView ? getSafeAreaStyle() : {}}
        >
          {/* 네비게이션 (데스크톱만) */}
          {!isMobileView && (
            <nav className="shrink-0">
            </nav>
          )}

          {/* 메인 카드 컨테이너 */}
          <div className={`flex flex-col overflow-hidden rounded-3xl bg-white shadow-lg flex-1 ${
            isMobileView ? 'h-0' : ''
          }`}>
            {/* 헤더 영역 */}
            <div className="shrink-0 border-b bg-white">
              <div style={{ 
                padding: isMobileView ? '14.4px 14.4px 0 14.4px' : '16px 16px 0 16px',
                marginBottom: isMobileView ? '0' : '0'
              }}>
                <div className={`${isMobileView ? 'pb-4' : 'p-4'} rounded-lg`}>
                  <div className="mb-4 flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      <h3 className="text-lg font-semibold">문제 관리</h3>
                      {!loading && problems.length > 0 && (
                        <p className="text-xs text-gray-500">
                          - 총 <span className="font-semibold text-primary">{totalItems || 0}</span>개 중 <span className="font-semibold text-primary">{problems.length}</span>개 표시 중
                        </p>
                      )}
                    </div>
                    {/* 모바일: 라벨일괄수정 버튼만 우측에 표시 */}
                    {isMobileView && (
                      <Button
                        variant="secondary"
                        onClick={() => setShowBulkLabelModal(true)}
                        className="text-xs rounded-3xl whitespace-nowrap"
                      >
                        라벨일괄수정
                      </Button>
                    )}
                    {/* 데스크톱: 문제등록 버튼 및 라벨일괄수정 버튼 - 우측 상단으로 이동 */}
                    {!isMobileView && (
                      <div className="flex gap-2">
                        <Button
                          variant="secondary"
                          onClick={() => setShowBulkLabelModal(true)}
                          className="text-xs rounded-3xl"
                        >
                          라벨일괄수정
                        </Button>
                        <Button
                          variant="primary"
                          onClick={() => navigate('/problem-register')}
                          className="text-xs rounded-3xl"
                        >
                          문제등록
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  {/* 필터 및 액션 버튼 */}
                  <div className={isMobileView ? "space-y-2" : ""}>
                    {/* 모바일 뷰 - 기존 레이아웃 유지 */}
                    {isMobileView ? (
                      <>
                        {/* Problem ID 검색 섹션 */}
                        <div className="flex gap-2">
                          <div className="flex flex-1 flex-col gap-2">
                            <input
                              type="number"
                              placeholder="문제 ID 검색"
                              value={problemIdSearch}
                              onChange={(e) => setProblemIdSearch(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && problemIdSearch && problemIdSearch.trim()) {
                                  e.preventDefault();
                                  handleProblemIdSearch();
                                }
                              }}
                              className="w-full flex-1 rounded border px-2 py-2 text-xs"
                              min="1"
                            />
                          </div>
                          <div className="flex w-16 flex-col gap-2">
                            <Button
                              variant="primary"
                              onClick={handleProblemIdSearch}
                              className="w-16 bg-headerBg px-3 py-2 text-xs hover:bg-gray-700"
                              size="sm"
                              rounded="3xl"
                            >
                              ID 검색
                            </Button>
                          </div>
                        </div>
                        
                        {/* 필터 입력 필드들 */}
                        <div className="flex gap-2">
                          <div className="flex flex-1 flex-col gap-2">
                            <input
                              type="text"
                              name="author"
                              placeholder="작성자"
                              value={filters.author}
                              onChange={handleFilterChange}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  const hasFilterValues = filters.status || filters.author || filters.labels;
                                  if (hasFilterValues) {
                                    e.preventDefault();
                                    handleSearch();
                                  }
                                }
                              }}
                              className="w-full flex-1 rounded border px-2 py-1 text-xs"
                            />
                            <input
                              type="text"
                              name="labels"
                              placeholder="라벨 키워드 검색(','콤마로 분류)"
                              value={filters.labels}
                              onChange={handleFilterChange}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  const hasFilterValues = filters.status || filters.author || filters.labels;
                                  if (hasFilterValues) {
                                    e.preventDefault();
                                    handleSearch();
                                  }
                                }
                              }}
                              className="w-full flex-1 rounded border px-2 py-1 text-xs"
                            />
                          </div>
                          <div className="flex w-16 flex-col gap-2">
                            <Button
                              onClick={handleReset}
                              variant="secondary"
                              className="w-16 px-3 py-1"
                              size="sm"
                              rounded="3xl"
                            >
                              초기화
                            </Button>
                            <Button
                              onClick={handleSearch}
                              variant="primary"
                              className="w-16 bg-headerBg px-3 py-1 hover:bg-gray-700"
                              size="sm"
                              rounded="3xl"
                            >
                              조회
                            </Button>
                          </div>
                        </div>
                      </>
                    ) : (
                      /* 데스크톱 뷰 - 한 줄 레이아웃 */
                      <div className="flex gap-2 items-center">
                        <input
                          type="number"
                          placeholder="문제 ID 검색"
                          value={problemIdSearch}
                          onChange={(e) => setProblemIdSearch(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && problemIdSearch && problemIdSearch.trim()) {
                              e.preventDefault();
                              handleProblemIdSearch();
                            }
                          }}
                          className="flex-1 rounded border px-3 py-2.5 text-sm"
                          min="1"
                        />
                        <Button
                          variant="primary"
                          onClick={handleProblemIdSearch}
                          className="px-6 py-2.5 text-sm"
                          size="sm"
                          rounded="3xl"
                        >
                          ID 검색
                        </Button>
                        <input
                          type="text"
                          name="author"
                          placeholder="작성자"
                          value={filters.author}
                          onChange={handleFilterChange}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              const hasFilterValues = filters.status || filters.author || filters.labels;
                              if (hasFilterValues) {
                                e.preventDefault();
                                handleSearch();
                              }
                            }
                          }}
                          className="flex-1 rounded border px-3 py-2.5 text-sm"
                        />
                        <input
                          type="text"
                          name="labels"
                          placeholder="라벨 키워드 검색(','콤마로 분류)"
                          value={filters.labels}
                          onChange={handleFilterChange}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              const hasFilterValues = filters.status || filters.author || filters.labels;
                              if (hasFilterValues) {
                                e.preventDefault();
                                handleSearch();
                              }
                            }
                          }}
                          className="flex-1 rounded border px-3 py-2.5 text-sm"
                        />
                        <Button
                          onClick={handleReset}
                          variant="secondary"
                          className="px-6 py-2.5 text-sm"
                          size="sm"
                          rounded="3xl"
                        >
                          초기화
                        </Button>
                        <Button
                          onClick={handleSearch}
                          variant="primary"
                          className="px-6 py-2.5 text-sm"
                          size="sm"
                          rounded="3xl"
                        >
                          조회
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* 컨텐츠 영역 - 데스크톱 */}
            {!isMobileView && (
            <div className="flex-1 flex flex-col" style={{ 
              padding: '0',
              marginTop: '16px',
              overflow: 'hidden'
            }}>
              <div className="px-4 pb-4 flex-1 flex flex-col overflow-hidden">
                <div ref={tableContainerRef} className="flex-1 overflow-auto" style={{ minHeight: 0, width: '100%' }}>
                  <style>{`
                    tbody tr.problem-row td {
                      padding-bottom: 0 !important;
                      transition: background-color 0.2s ease;
                    }
                    tbody tr.solution-row td {
                      padding-top: 0 !important;
                      transition: background-color 0.2s ease;
                    }
                    /* 작업 칼럼의 행 간 구분선 제거 */
                    tbody tr td:last-child {
                      border-bottom: none !important;
                      border-top: none !important;
                    }
                    /* 테이블 레이아웃 고정 */
                    table {
                      table-layout: fixed;
                      width: 100%;
                    }
                    /* 텍스트 오버플로우 처리 */
                    .truncate-cell {
                      overflow: hidden;
                      text-overflow: ellipsis;
                      white-space: nowrap;
                    }
                  `}</style>
                  <table className="w-full">
                    <colgroup>
                      <col style={{ width: '60px' }} />
                      <col style={{ width: '60px' }} />
                      <col style={{ width: '100px' }} />
                      <col />
                      <col style={{ width: '80px' }} />
                      <col style={{ width: '40px' }} />
                      <col style={{ width: '140px' }} />
                      <col style={{ width: '140px' }} />
                      <col style={{ width: '140px' }} />
                    </colgroup>
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">PID</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">상태</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">작성자</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">라벨</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">정답</th>
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">난이도</th>
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">생성일</th>
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">수정일</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">작업</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {problems.map((problem) => (
                        <Fragment key={problem.problem_id}>
                          {/* 첫 번째 행 - 문제 데이터 */}
                          <tr 
                            className="problem-row cursor-pointer transition-all duration-200"
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={(e) => handleMouseUp(e, problem)}
                            onMouseEnter={(e) => {
                              const current = e.currentTarget;
                              const next = current.nextElementSibling;
                              // 각 셀에 개별적으로 배경색 적용
                              const currentCells = current.querySelectorAll('td');
                              const nextCells = next?.querySelectorAll('td');
                              currentCells.forEach(cell => cell.classList.add('bg-primary/10'));
                              nextCells?.forEach(cell => cell.classList.add('bg-primary/10'));
                              // 좌측 라운드(rowSpan 셀 - 상하 모두)
                              const leftSpanCell = current.querySelector('td:first-child');
                              leftSpanCell?.classList.add('rounded-l-3xl');
                              // 우측 라운드(첫 번째 행은 상단, 두 번째 행은 하단)
                              const rightTop = current.querySelector('td:last-child');
                              const rightBottom = next?.querySelector('td:last-child');
                              rightTop?.classList.add('rounded-tr-3xl');
                              rightBottom?.classList.add('rounded-br-3xl');
                            }}
                            onMouseLeave={(e) => {
                              const current = e.currentTarget;
                              const next = current.nextElementSibling;
                              // 각 셀에서 개별적으로 배경색 제거
                              const currentCells = current.querySelectorAll('td');
                              const nextCells = next?.querySelectorAll('td');
                              currentCells.forEach(cell => cell.classList.remove('bg-primary/10'));
                              nextCells?.forEach(cell => cell.classList.remove('bg-primary/10'));
                              const leftSpanCell = current.querySelector('td:first-child');
                              leftSpanCell?.classList.remove('rounded-l-3xl');
                              const rightTop = current.querySelector('td:last-child');
                              const rightBottom = next?.querySelector('td:last-child');
                              rightTop?.classList.remove('rounded-tr-3xl');
                              rightBottom?.classList.remove('rounded-br-3xl');
                            }}
                          >
                            <td rowSpan="2" className="px-3 py-1 whitespace-nowrap text-center align-middle">
                              <span className="text-sm text-gray-900 font-semibold">{problem.problem_id}</span>
                            </td>
                            <td className="px-3 py-1 whitespace-nowrap text-center">
                              <div className="flex gap-2 justify-center items-center">
                                <div className="flex gap-0.5">
                                  <span className={`size-2 rounded-full ${getStatusInfo(problem.status).image ? 'bg-success' : 'bg-danger'}`} title="이미지" />
                                  <span className={`size-2 rounded-full ${getStatusInfo(problem.status).ocr ? 'bg-success' : 'bg-danger'}`} title="OCR" />
                                  <span className={`size-2 rounded-full ${getStatusInfo(problem.status).editor ? 'bg-success' : 'bg-danger'}`} title="에디터" />
                                </div>
                                {aiProblemStatuses[problem.problem_id]?.status === 777 && (
                                  <span className="px-1.5 py-0.5 text-xs font-medium bg-purple-100 text-purple-800 rounded-full" title="AI 생성 문제">
                                    AI
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-1 whitespace-nowrap">
                              <div 
                                className="text-sm text-gray-900 truncate-cell"
                                onMouseEnter={(e) => {
                                  if (e.currentTarget.scrollWidth > e.currentTarget.clientWidth) {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setTooltip({
                                      show: true,
                                      text: problem.author,
                                      x: rect.left,
                                      y: rect.top - 30,
                                      isHtml: false
                                    });
                                  }
                                }}
                                onMouseLeave={() => setTooltip({ show: false, text: '', x: 0, y: 0, isHtml: false })}
                              >
                                {problem.author}
                              </div>
                            </td>
                            <td rowSpan="2" className="px-3 py-1 align-middle">
                              <div className="flex items-center justify-between">
                                <div 
                                  className="text-sm text-gray-900 truncate-cell flex-1"
                                  onMouseEnter={(e) => {
                                    if (problem.labels && e.currentTarget.scrollWidth > e.currentTarget.clientWidth) {
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      setTooltip({
                                        show: true,
                                        text: problem.labels,
                                        x: rect.left,
                                        y: rect.top - 30,
                                        isHtml: false
                                      });
                                    }
                                  }}
                                  onMouseLeave={() => setTooltip({ show: false, text: '', x: 0, y: 0, isHtml: false })}
                                >
                                  {problem.labels || '-'}
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleLabelEdit(problem);
                                  }}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onMouseUp={(e) => e.stopPropagation()}
                                  className="ml-2 p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-50 active:bg-blue-100 rounded-md transition-all duration-200"
                                  title="라벨 수정"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                            <td rowSpan="2" className="px-2 py-1 align-middle">
                              <div 
                                className="overflow-hidden relative cursor-pointer"
                                style={{ 
                                  maxWidth: '100px',
                                  maxHeight: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'flex-start'
                                }}
                                onMouseEnter={(e) => {
                                  const answer = problem.solution_data?.[0]?.solution_info?.answer;
                                  if (answer) {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    
                                    // 툴팁용 LaTeX 렌더링을 위한 임시 div 생성
                                    const tempDiv = document.createElement('div');
                                    tempDiv.style.position = 'absolute';
                                    tempDiv.style.visibility = 'hidden';
                                    document.body.appendChild(tempDiv);
                                    
                                    try {
                                      const cleanAnswer = answer.startsWith('$') && answer.endsWith('$')
                                        ? answer.slice(1, -1)
                                        : answer;
                                      
                                      // KaTeX 렌더링 시도
                                      katex.render(cleanAnswer, tempDiv, { throwOnError: false });
                                      
                                      setTooltip({
                                        show: true,
                                        text: tempDiv.innerHTML,
                                        x: rect.left,
                                        y: rect.top - 40,
                                        isHtml: true
                                      });
                                    } catch (error) {
                                      // 렌더링 실패 시 원본 텍스트 표시
                                      setTooltip({
                                        show: true,
                                        text: answer,
                                        x: rect.left,
                                        y: rect.top - 30,
                                        isHtml: false
                                      });
                                    } finally {
                                      document.body.removeChild(tempDiv);
                                    }
                                  }
                                }}
                                onMouseLeave={() => setTooltip({ show: false, text: '', x: 0, y: 0, isHtml: false })}
                              >
                                {(() => {
                                  const answer = problem.solution_data?.[0]?.solution_info?.answer;
                                  if (!answer) return <span className="text-xs text-gray-400">-</span>;
                                  
                                  // 여러 방법으로 LaTeX 렌더링 시도
                                  const renderMath = () => {
                                    // 1. 델리미터 제거
                                    let cleanAnswer = answer.trim();
                                    
                                    // 다양한 델리미터 처리
                                    if (cleanAnswer.startsWith('$') && cleanAnswer.endsWith('$')) {
                                      cleanAnswer = cleanAnswer.slice(1, -1);
                                    } else if (cleanAnswer.startsWith('\\(') && cleanAnswer.endsWith('\\)')) {
                                      cleanAnswer = cleanAnswer.slice(2, -2);
                                    } else if (cleanAnswer.startsWith('\\[') && cleanAnswer.endsWith('\\]')) {
                                      cleanAnswer = cleanAnswer.slice(2, -2);
                                    }
                                    
                                    // 2. 빈 문자열 체크
                                    if (!cleanAnswer) {
                                      return <span className="text-xs text-gray-400">-</span>;
                                    }
                                    
                                    // 3. 숫자만 있는 경우 바로 표시
                                    if (/^[0-9\-\.\,]+$/.test(cleanAnswer)) {
                                      return <span className="text-xs font-medium">{cleanAnswer}</span>;
                                    }
                                    
                                    // 4. LaTeX 렌더링 시도
                                    try {
                                      // 긴 수식은 폰트 크기 줄이기
                                      const fontSize = cleanAnswer.length > 20 ? 'text-[10px]' : 
                                                      cleanAnswer.length > 15 ? 'text-[11px]' : 'text-xs';
                                      
                                      return (
                                        <div className={fontSize}>
                                          <InlineMath math={cleanAnswer} />
                                        </div>
                                      );
                                    } catch (katexError) {
                                      // 5. KaTeX 실패 시 대체 처리
                                      console.warn('KaTeX 렌더링 실패:', cleanAnswer, katexError);
                                      
                                      // 일반 텍스트로 표시 (overflow hidden으로 처리)
                                      return (
                                        <span 
                                          className="text-xs font-mono"
                                          style={{
                                            display: 'block',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            maxWidth: '90px'
                                          }}
                                        >
                                          {answer}
                                        </span>
                                      );
                                    }
                                  };
                                  
                                  return renderMath();
                                })()}
                              </div>
                            </td>
                            <td rowSpan="2" className="px-2 py-1 whitespace-nowrap text-center align-middle">
                              {aiProblemStatuses[problem.problem_id]?.difficulty ? (
                                <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium ${
                                  aiProblemStatuses[problem.problem_id].difficulty === 1 ? 'bg-yellow-100 text-yellow-600' :
                                  aiProblemStatuses[problem.problem_id].difficulty === 2 ? 'bg-green-100 text-green-600' :
                                  aiProblemStatuses[problem.problem_id].difficulty === 3 ? 'bg-blue-100 text-blue-600' :
                                  aiProblemStatuses[problem.problem_id].difficulty === 4 ? 'bg-orange-100 text-orange-600' :
                                  aiProblemStatuses[problem.problem_id].difficulty === 5 ? 'bg-red-100 text-red-600' :
                                  'bg-gray-100 text-gray-600'
                                }`}>
                                  {aiProblemStatuses[problem.problem_id].difficulty}
                                </span>
                              ) : (
                                <span className="text-sm text-gray-400">-</span>
                              )}
                            </td>
                            <td className="px-2 py-1 whitespace-nowrap text-center">
                              <span className="text-xs text-gray-700">{formatDate(problem.created_at)}</span>
                            </td>
                            <td className="px-2 py-1 whitespace-nowrap text-center">
                              <span className="text-xs text-gray-700">{formatDate(problem.updated_at)}</span>
                            </td>
                            <td className="px-2 py-1 whitespace-nowrap text-center">
                              <div className="flex gap-0.5 justify-center">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onMouseUp={(e) => e.stopPropagation()}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEdit(problem);
                                  }}
                                  className="text-xs text-primary hover:text-primary-dark py-0.5 px-1"
                                >
                                  문제수정
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onMouseUp={(e) => e.stopPropagation()}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(problem.problem_id);
                                  }}
                                  className="text-xs text-danger hover:text-red-700 py-0.5 px-1"
                                >
                                  문제삭제
                                </Button>
                              </div>
                            </td>
                          </tr>
                          {/* 두 번째 행 - 풀이 데이터 */}
                          <tr 
                            className="solution-row cursor-pointer transition-all duration-200 border-b border-gray-300"
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={(e) => handleMouseUp(e, problem)}
                            onMouseEnter={(e) => {
                              const current = e.currentTarget;
                              const prev = current.previousElementSibling;
                              // 각 셀에 개별적으로 배경색 적용
                              const prevCells = prev?.querySelectorAll('td');
                              const currentCells = current.querySelectorAll('td');
                              prevCells?.forEach(cell => cell.classList.add('bg-primary/10'));
                              currentCells.forEach(cell => cell.classList.add('bg-primary/10'));
                              // 좌측 라운드(rowSpan 셀 - 상하 모두)
                              const leftSpanCell = prev?.querySelector('td:first-child');
                              leftSpanCell?.classList.add('rounded-l-3xl');
                              // 우측 라운드(첫 번째 행은 상단, 두 번째 행은 하단)
                              const rightTop = prev?.querySelector('td:last-child');
                              const rightBottom = current.querySelector('td:last-child');
                              rightTop?.classList.add('rounded-tr-3xl');
                              rightBottom?.classList.add('rounded-br-3xl');
                            }}
                            onMouseLeave={(e) => {
                              const current = e.currentTarget;
                              const prev = current.previousElementSibling;
                              // 각 셀에서 개별적으로 배경색 제거
                              const prevCells = prev?.querySelectorAll('td');
                              const currentCells = current.querySelectorAll('td');
                              prevCells?.forEach(cell => cell.classList.remove('bg-primary/10'));
                              currentCells.forEach(cell => cell.classList.remove('bg-primary/10'));
                              const leftSpanCell = prev?.querySelector('td:first-child');
                              leftSpanCell?.classList.remove('rounded-l-3xl');
                              const rightTop = prev?.querySelector('td:last-child');
                              const rightBottom = current.querySelector('td:last-child');
                              rightTop?.classList.remove('rounded-tr-3xl');
                              rightBottom?.classList.remove('rounded-br-3xl');
                            }}
                          >
                            <td className="px-3 py-1 whitespace-nowrap text-center">
                              <div className="flex gap-0.5 justify-center">
                                <span 
                                  className={`size-2 rounded-full ${
                                    getSolutionStatusInfo(problem.solution_data?.[0]?.solution_info?.status || 0).answer 
                                      ? 'bg-green-500' 
                                      : 'bg-red-500'
                                  }`} 
                                  title="정답" 
                                />
                                <span 
                                  className={`size-2 rounded-full ${
                                    getSolutionStatusInfo(problem.solution_data?.[0]?.solution_info?.status || 0).image 
                                      ? 'bg-green-500' 
                                      : 'bg-red-500'
                                  }`} 
                                  title="이미지" 
                                />
                                <span 
                                  className={`size-2 rounded-full ${
                                    getSolutionStatusInfo(problem.solution_data?.[0]?.solution_info?.status || 0).ocr 
                                      ? 'bg-green-500' 
                                      : 'bg-red-500'
                                  }`} 
                                  title="OCR" 
                                />
                                <span 
                                  className={`size-2 rounded-full ${
                                    getSolutionStatusInfo(problem.solution_data?.[0]?.solution_info?.status || 0).editor 
                                      ? 'bg-green-500' 
                                      : 'bg-red-500'
                                  }`} 
                                  title="에디터" 
                                />
                              </div>
                            </td>
                            <td className="px-3 py-1 whitespace-nowrap">
                              <span className="text-sm text-gray-900">{problem.solution_data?.[0]?.solution_info?.author || '-'}</span>
                            </td>
                            <td className="px-2 py-1 whitespace-nowrap text-center">
                              <span className="text-xs text-gray-700">
                                {problem.solution_data?.[0]?.solution_info?.created_at ? formatDate(problem.solution_data[0].solution_info.created_at) : '-'}
                              </span>
                            </td>
                            <td className="px-2 py-1 whitespace-nowrap text-center">
                              <span className="text-xs text-gray-700">
                                {problem.solution_data?.[0]?.solution_info?.updated_at ? formatDate(problem.solution_data[0].solution_info.updated_at) : '-'}
                              </span>
                            </td>
                            <td className="px-2 py-1 whitespace-nowrap text-center">
                              <div className="flex gap-0.5 justify-center">
                                {problem.solution_data?.[0]?.solution_info?.status >= 100 ? (
                                  <>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onMouseDown={(e) => e.stopPropagation()}
                                      onMouseUp={(e) => e.stopPropagation()}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleAnswerEdit(problem.problem_id);
                                      }}
                                      className="text-xs text-purple-600 hover:text-purple-700 py-0.5 px-1"
                                    >
                                      정답수정
                                    </Button>
                                    {problem.solution_data?.[0]?.solution_info?.status >= 200 ? (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onMouseUp={(e) => e.stopPropagation()}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleSolutionEdit(problem.problem_id);
                                        }}
                                        className="text-xs text-orange-600 hover:text-orange-700 py-0.5 px-1"
                                      >
                                        풀이수정
                                      </Button>
                                    ) : (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onMouseUp={(e) => e.stopPropagation()}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleSolutionRegister(problem.problem_id);
                                        }}
                                        className="text-xs text-headerBg hover:text-gray-700 py-0.5 px-1"
                                      >
                                        풀이등록
                                      </Button>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onMouseDown={(e) => e.stopPropagation()}
                                      onMouseUp={(e) => e.stopPropagation()}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleAnswerRegister(problem.problem_id);
                                      }}
                                      className="text-xs text-headerBg hover:text-gray-700 py-0.5 px-1"
                                    >
                                      정답등록
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onMouseDown={(e) => e.stopPropagation()}
                                      onMouseUp={(e) => e.stopPropagation()}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleSolutionRegister(problem.problem_id);
                                      }}
                                      className="cursor-not-allowed text-xs text-gray-400 py-0.5 px-1"
                                      disabled
                                    >
                                      풀이등록
                                    </Button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                          {/* 세 번째 행 - 문제 내용 미리보기 */}
                          {/* 데스크톱에서는 미리보기 비활성화 (모바일에만 적용) */}
                          {/* {problemContents[problem.problem_id] && (
                            <tr 
                              className="preview-row cursor-pointer transition-all duration-200 border-b border-gray-300"
                              onMouseDown={handleMouseDown}
                              onMouseMove={handleMouseMove}
                              onMouseUp={(e) => handleMouseUp(e, problem)}
                              onMouseEnter={(e) => {
                                e.currentTarget.classList.add('row-hover');
                                // 첫 번째, 두 번째 행도 호버 효과 적용
                                const solutionRow = e.currentTarget.previousElementSibling;
                                const problemRow = solutionRow?.previousElementSibling;
                                solutionRow?.classList.add('row-hover');
                                problemRow?.classList.add('row-hover');
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.classList.remove('row-hover');
                                // 첫 번째, 두 번째 행도 호버 효과 제거
                                const solutionRow = e.currentTarget.previousElementSibling;
                                const problemRow = solutionRow?.previousElementSibling;
                                solutionRow?.classList.remove('row-hover');
                                problemRow?.classList.remove('row-hover');
                              }}
                            >
                              <td colSpan="8" className="p-3">
                                <div className="relative rounded-lg bg-gray-50 p-3">
                                  <div 
                                    className="problem-preview-content text-sm"
                                    dangerouslySetInnerHTML={{ __html: problemContents[problem.problem_id] }}
                                    style={{ 
                                      maxHeight: '100px', 
                                      overflow: 'hidden'
                                    }}
                                  />
                                  <div 
                                    className="absolute bottom-0 left-0 right-0 h-8 rounded-b-lg bg-gradient-to-t from-gray-50 to-transparent"
                                    style={{ pointerEvents: 'none' }}
                                  />
                                </div>
                              </td>
                            </tr>
                          )} */}
                        </Fragment>
                  ))}
                    </tbody>
                  </table>
                </div>
                
                                 {/* 데스크톱 페이지네이션 */}
                 {!loading && (
                   <div className="shrink-0">
                     <DesktopPagination
                       currentPage={page}
                       totalPages={totalPages}
                       onPageChange={(newPage) => {
                         updateURL(newPage);
                         fetchProblems(newPage, Object.values(filters).some(f => f !== ''), false);
                       }}
                       itemsPerPage={calculateItemsPerPage()}
                       totalItems={totalItems}
                       showInfo={true}
                     />
                   </div>
                 )}
              </div>
            </div>
            )}

            {/* 모바일 카드 리스트 */}
            {isMobileView && (
            <div 
              ref={scrollContainerRef}
              className="flex-1 overflow-y-auto bg-gray-50 p-4"
              onScroll={handleMobileScroll}
              style={{ minHeight: 0 }}
            >
              <div className="space-y-3">
              {!loading && problems.map((problem) => (
                <div key={problem.problem_id} className="rounded-3xl border border-gray-200 bg-white p-3 shadow-lg overflow-hidden transition-all duration-300 hover:shadow-2xl hover:scale-[1.02] hover:border-primary/20">
                  <div className="mb-2 flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2.5">
                        <div 
                          className="text-2xl font-bold self-center"
                          style={{ 
                            color: '#4ABC85'
                          }}
                        >
                          {problem.problem_id}
                        </div>
                        {aiProblemStatuses[problem.problem_id]?.difficulty && (
                          <div 
                            className="rounded-full px-2 py-0.5 text-[10px] font-bold text-white"
                            style={{ 
                              backgroundColor: 
                                aiProblemStatuses[problem.problem_id].difficulty === 1 ? 'rgba(253, 224, 71, 0.7)' :
                                aiProblemStatuses[problem.problem_id].difficulty === 2 ? 'rgba(134, 239, 172, 0.7)' :
                                aiProblemStatuses[problem.problem_id].difficulty === 3 ? 'rgba(147, 197, 253, 0.7)' :
                                aiProblemStatuses[problem.problem_id].difficulty === 4 ? 'rgba(253, 186, 116, 0.7)' :
                                aiProblemStatuses[problem.problem_id].difficulty === 5 ? 'rgba(252, 165, 165, 0.7)' :
                                'rgba(156, 163, 175, 0.7)'
                            }}
                          >
                            Lv.{aiProblemStatuses[problem.problem_id].difficulty}
                          </div>
                        )}
                        <span className="text-xs text-gray-600 self-center">({problem.author})</span>
                        <Trash2 
                          size={14} 
                          className="cursor-pointer text-red-500 hover:text-red-700"
                          onClick={() => handleDelete(problem.problem_id)}
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1">
                        <span className={`size-2 rounded-full ${getStatusInfo(problem.status).image ? 'bg-success' : 'bg-danger'}`} title="이미지" />
                        <span className={`size-2 rounded-full ${getStatusInfo(problem.status).ocr ? 'bg-success' : 'bg-danger'}`} title="OCR" />
                        <span className={`size-2 rounded-full ${getStatusInfo(problem.status).editor ? 'bg-success' : 'bg-danger'}`} title="에디터" />
                      </div>
                      {aiProblemStatuses[problem.problem_id]?.status === 777 && (
                        <span className="px-1.5 py-0.5 text-[10px] font-medium bg-purple-100 text-purple-800 rounded-full" title="AI 생성 문제">
                          AI
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* 문제 관련 데이터 */}
                  <div className="text-xs min-w-0">
                    <div className="rounded-3xl mb-4">
                      <div className="flex items-center justify-between gap-2 min-w-0 mb-2 bg-gray-50 rounded-3xl px-3 py-1">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-medium shrink-0">라벨:</span> 
                          <span className="truncate min-w-0" title={problem.labels}>{problem.labels}</span>
                        </div>
                        <Edit2 
                          size={16} 
                          className="cursor-pointer text-blue-600 hover:text-blue-700 shrink-0"
                          onClick={() => handleLabelEdit(problem)}
                        />
                      </div>
                      
                      <div className="flex items-center justify-between text-gray-500">
                        <div><span className="font-medium">생성일:</span> {formatDate(problem.created_at)}</div>
                        <div className="mx-2 h-3 w-px bg-gray-300"></div>
                        <div><span className="font-medium">수정일:</span> {formatDate(problem.updated_at)}</div>
                      </div>
                    </div>
                    
                    {/* 문제 내용 미리보기 추가 */}
                    {problemContents[problem.problem_id] ? (
                      <div className="relative rounded-3xl border border-gray-200 bg-white p-2 pb-6 flex items-center shadow-xl z-10">
                        <div className="flex-1 pr-8">
                          <div 
                            className="problem-preview-content text-xs"
                            dangerouslySetInnerHTML={{ __html: problemContents[problem.problem_id] }}
                            style={{ 
                              maxHeight: '80px', 
                              overflow: 'hidden',
                              lineHeight: '1.4'
                            }}
                          />
                          <div 
                            className="absolute bottom-0 left-0 right-16 h-6 rounded-b-3xl bg-gradient-to-t from-white to-transparent"
                            style={{ pointerEvents: 'none' }}
                          />
                        </div>
                        {/* 문제 관련 버튼들 - 미리보기 영역 우측 중앙 */}
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex flex-col gap-2">
                          <Edit2
                            size={16}
                            className="cursor-pointer text-primary hover:text-primary-dark"
                            onClick={() => handleEdit(problem)}
                            title="문제 수정"
                          />
                          <Eye
                            size={16}
                            className="cursor-pointer text-success hover:text-green-700"
                            onClick={() => handlePreview(problem)}
                            title="미리보기"
                          />
                        </div>
                      </div>
                    ) : loadingContents[problem.problem_id] ? (
                      <div className="relative mt-2 rounded-3xl border border-gray-200 bg-white p-2 shadow-sm">
                        <div className="flex h-[80px] items-center justify-center text-xs text-gray-500">
                          로딩 중...
                        </div>
                        {/* 문제 관련 버튼들 - 로딩 영역 우측 중앙 */}
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex flex-col gap-2">
                          <Edit2
                            size={16}
                            className="cursor-pointer text-primary hover:text-primary-dark"
                            onClick={() => handleEdit(problem)}
                            title="문제 수정"
                          />
                          <Eye
                            size={16}
                            className="cursor-pointer text-success hover:text-green-700"
                            onClick={() => handlePreview(problem)}
                            title="미리보기"
                          />
                        </div>
                      </div>
                    ) : (
                      /* 미리보기가 없을 때도 버튼 표시를 위한 빈 영역 */
                      <div className="relative mt-2 rounded-3xl border border-gray-200 bg-white p-2 shadow-sm">
                        <div className="flex h-[80px] items-center justify-center text-xs text-gray-400">
                          문제 내용을 불러오는 중...
                        </div>
                        {/* 문제 관련 버튼들 - 빈 영역 우측 중앙 */}
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex flex-col gap-2">
                          <Edit2
                            size={16}
                            className="cursor-pointer text-primary hover:text-primary-dark"
                            onClick={() => handleEdit(problem)}
                            title="문제 수정"
                          />
                          <Eye
                            size={16}
                            className="cursor-pointer text-success hover:text-green-700"
                            onClick={() => handlePreview(problem)}
                            title="미리보기"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* 풀이 관련 데이터 - 서랍 스타일 */}
                    <div className="text-xs min-w-0 bg-gray-100 rounded-b-3xl px-4 pt-3 pb-3 -mt-8 relative shadow-inner">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">정답/풀이 정보</span>
                        {problem.solution_data?.[0]?.solution_info?.author && (
                          <span className="text-xs text-gray-500">({problem.solution_data[0].solution_info.author})</span>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <span 
                          className={`size-2 rounded-full ${
                            getSolutionStatusInfo(problem.solution_data?.[0]?.solution_info?.status || 0).answer 
                              ? 'bg-green-500' 
                              : 'bg-red-500'
                          }`} 
                          title="정답" 
                        />
                        <span 
                          className={`size-2 rounded-full ${
                            getSolutionStatusInfo(problem.solution_data?.[0]?.solution_info?.status || 0).image 
                              ? 'bg-green-500' 
                              : 'bg-red-500'
                          }`} 
                          title="이미지" 
                        />
                        <span 
                          className={`size-2 rounded-full ${
                            getSolutionStatusInfo(problem.solution_data?.[0]?.solution_info?.status || 0).ocr 
                              ? 'bg-green-500' 
                              : 'bg-red-500'
                          }`} 
                          title="OCR" 
                        />
                        <span 
                          className={`size-2 rounded-full ${
                            getSolutionStatusInfo(problem.solution_data?.[0]?.solution_info?.status || 0).editor 
                              ? 'bg-green-500' 
                              : 'bg-red-500'
                          }`} 
                          title="에디터" 
                        />
                      </div>
                    </div>
                    {/* 정답 영역 항상 표시 */}
                    <div className={`relative flex items-center justify-center break-all px-3 py-1 min-h-[32px] bg-gray-50 rounded-3xl ${problem.solution_data?.[0]?.solution_info?.answer && problem.solution_data?.[0]?.solution_info?.status >= 100 ? 'pr-16' : ''}`}>
                      {problem.solution_data?.[0]?.solution_info?.answer ? (
                        <div className="w-full text-left">
                          <span className="font-medium">정답:   </span> 
                          {(() => {
                            const answer = problem.solution_data?.[0]?.solution_info?.answer;
                            try {
                              // $...$ 델리미터 제거 (있는 경우)
                              const cleanAnswer = answer.startsWith('$') && answer.endsWith('$') 
                                ? answer.slice(1, -1) 
                                : answer;
                              return <InlineMath math={cleanAnswer} />;
                            } catch (error) {
                              console.error('KaTeX 렌더링 에러:', error);
                              return answer;
                            }
                          })()}
                        </div>
                      ) : problem.solution_data?.[0]?.solution_info?.status >= 100 ? (
                        <span className="text-xs text-gray-400">정답 없음</span>
                      ) : (
                        <Plus
                          size={16}
                          className="cursor-pointer text-headerBg hover:text-gray-700"
                          onClick={() => handleAnswerRegister(problem.problem_id)}
                          title="정답등록"
                        />
                      )}
                      {/* 정답 수정, 풀이 등록/수정 버튼 */}
                      {problem.solution_data?.[0]?.solution_info?.status >= 100 && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex gap-2">
                          <Target
                            size={16}
                            className="cursor-pointer text-blue-600 hover:text-blue-700"
                            onClick={() => handleAnswerEdit(problem.problem_id)}
                            title="정답수정"
                          />
                          {problem.solution_data?.[0]?.solution_info?.status >= 200 ? (
                            <FileText
                              size={16}
                              className="cursor-pointer text-blue-600 hover:text-blue-700"
                              onClick={() => handleSolutionEdit(problem.problem_id)}
                              title="풀이수정"
                            />
                          ) : (
                            <Plus
                              size={16}
                              className="cursor-pointer text-headerBg hover:text-gray-700"
                              onClick={() => handleSolutionRegister(problem.problem_id)}
                              title="풀이등록"
                            />
                          )}
                        </div>
                      )}
                    </div>
                    {problem.solution_data?.[0]?.solution_info && (
                      <div className="flex items-center justify-between text-gray-500 mt-2">
                        <div><span className="font-medium">생성일:</span> {problem.solution_data[0].solution_info.created_at ? formatDate(problem.solution_data[0].solution_info.created_at) : '-'}</div>
                        <div className="mx-2 h-3 w-px bg-gray-300"></div>
                        <div><span className="font-medium">수정일:</span> {problem.solution_data[0].solution_info.updated_at ? formatDate(problem.solution_data[0].solution_info.updated_at) : '-'}</div>
                      </div>
                    )}
                  </div>

                </div>
              ))}
              
              {/* 인피니티 스크롤 로딩 인디케이터 */}
              {isLoadingMore && (
                <div className="flex justify-center py-4">
                  <div className="size-6 animate-spin rounded-full border-b-2 border-primary"></div>
                </div>
              )}
              
              {/* 더 이상 로드할 데이터가 없을 때 */}
              {!hasMore && problems.length > 0 && !isLoadingMore && (
                <div className="py-4 text-center text-sm text-gray-500">
                  모든 문제를 불러왔습니다.
                </div>
              )}
              
              {/* 로딩 중일 때 */}
              {loading && problems.length === 0 && (
                <div className="flex h-full items-center justify-center">
                  <div className="text-center">
                    <div className="size-8 animate-spin rounded-full border-b-2 border-primary mx-auto mb-2"></div>
                    <div className="text-sm text-gray-500">로딩 중...</div>
                  </div>
                </div>
              )}
              
              {/* 데이터가 없을 때 */}
              {!loading && problems.length === 0 && (
                <div className="flex h-full items-center justify-center">
                  <div className="text-center text-sm text-gray-500">
                    문제가 없습니다.
                  </div>
                </div>
              )}
              </div>
            </div>
            )}

            {/* 페이지네이션 제거 */}
            {/* {!loading && problems.length > 0 && (
              <div className="flex justify-center p-4 border-t">
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                    size="sm"
                  >
                    이전
                  </Button>
                  {isLoadingMore && (
                    <div className="animate-spin text-primary">⟳</div>
                  )}
                  <Button
                    variant="secondary"
                    onClick={() => handlePageChange(page + 1)}
                    disabled={!hasMore}
                    size="sm"
                  >
                    다음
                  </Button>
                </div>
              </div>
            )} */}

          </div>

          {/* 모바일 플로팅 문제 등록 버튼 */}
          {isMobileView && (
            <FloatingButton
              variant="plus"
              onClick={() => navigate('/problem-register')}
              title="문제 등록"
              usePortal={true}
            />
          )}

          {/* ProblemPreview 컴포넌트 */}
          <ProblemPreview
            isOpen={previewInfo.isOpen}
            onClose={() => setPreviewInfo({
              isOpen: false,
              problemId: [],
              problemNumber: null,
              isCorrect: null
            })}
            problemIds={previewInfo.problemId}
            showNumbers={false}  // 문제 수정 페이지에서는 번호 표시 안 함
            correctList={[]}     // 문제 수정 페이지에서는 정답 여부 없음
            problemNumbers={[]}  // 문제 수정 페이지에서는 번호 없음
          />

          {/* 기존 ProblemEditModal */}
          {showModal && selectedProblem && (
            <ProblemEditModal
              problem={selectedProblem}
              onClose={() => {
                setShowModal(false);
                setSelectedProblem(null);
              }}
              onUpdate={refreshDataKeepPage}
            />
          )}

          {/* 라벨 수정 모달 */}
          <LabelEditModal
            isOpen={labelModalInfo.isOpen}
            onClose={() => setLabelModalInfo({...labelModalInfo, isOpen: false})}
            problemId={labelModalInfo.problemId}
            initialLabels={labelModalInfo.labels}
            onSave={handleLabelSave}
          />

          {/* 풀이 수정 모달 */}
          {showSolutionModal && selectedSolution && (
            <SolutionEditModal
              solution={selectedSolution}
              onClose={() => {
                setShowSolutionModal(false);
                setSelectedSolution(null);
              }}
              onUpdate={refreshDataKeepPage}
            />
          )}

          {/* 정답 모달 */}
          {showAnswerModal && (
            <AnswerModal
              problemId={selectedProblemId}
              solution={selectedSolution}
              onClose={() => {
                setShowAnswerModal(false);
                setSelectedProblemId(null);
                setSelectedSolution(null);
                // refreshData 제거 - 취소 시 새로고침 하지 않음
              }}
              onUpdate={() => {
                // 정답 등록/수정 성공 시에만 해당 문제 데이터 업데이트
                // selectedProblemId를 미리 저장 (onClose 호출 전에 실행되도록)
                const currentProblemId = selectedProblemId;
                
                const updateSingleProblem = async () => {
                  try {
                    if (!currentProblemId) {
                      console.error('Problem ID is missing');
                      refreshData();
                      return;
                    }
                    
                    // 해당 문제의 솔루션 데이터만 다시 가져오기
                    const solutionsResponse = await fetchWithTokenRefresh(`/api/solutions/list/${currentProblemId}`);
                    
                    // 현재 표시된 문제 목록에서 해당 문제만 업데이트
                    setProblems(prevProblems => 
                      prevProblems.map(problem => 
                        problem.problem_id === currentProblemId
                          ? {
                              ...problem,
                              has_solution: (solutionsResponse?.data?.length ?? 0) > 0 && 
                                            solutionsResponse?.data?.[0]?.solution_info?.status >= 100,
                              solution_data: solutionsResponse?.data ?? []
                            }
                          : problem
                      )
                    );
                  } catch (error) {
                    console.error('문제 데이터 업데이트 실패:', error);
                    refreshData(); // 에러 시 전체 새로고침
                  }
                };
                updateSingleProblem();
              }}
            />
          )}

          {/* 풀이 등록 모달 */}
          <SolutionRegisterModal
            isOpen={showSolutionRegisterModal}
            onClose={() => {
              setShowSolutionRegisterModal(false);
              setSolutionRegisterProblemId(null);
            }}
            problemId={solutionRegisterProblemId}
            onUpdate={refreshDataKeepPage}
          />

          {/* 라벨 일괄 수정 모달 */}
          <BulkLabelModal
            isOpen={showBulkLabelModal}
            onClose={() => setShowBulkLabelModal(false)}
            fetchWithTokenRefresh={fetchWithTokenRefresh}
            onSuccess={refreshDataKeepPage}
          />

          {/* 툴팁 */}
          {tooltip.show && (
            <div
              className="fixed z-50 px-2 py-1 text-sm bg-gray-800 text-white rounded shadow-lg pointer-events-none"
              style={{
                left: `${tooltip.x}px`,
                top: `${tooltip.y}px`,
                maxWidth: '400px',
                wordBreak: 'break-all'
              }}
            >
              {tooltip.isHtml ? (
                <div dangerouslySetInnerHTML={{ __html: tooltip.text }} />
              ) : (
                tooltip.text
              )}
            </div>
          )}
        </div>
      )}
    />
  );
}

export default ProblemEdit;